/**
 * shared workers have limited access to standard browser apis (i.e. local
 * storage), this means there needs to be some chatter between clients and the
 * worker to exchange credentials
 *
 * additionally, theres no signal to determine when a client disconnects
 *
 * the communication flow is as follows
 *
 * sse server upstream -> sse worker <-> sse clients -> consumer
 */

import type { SseStreamResponseDto } from '@montugroup/pms-api-contracts';

export type SseConfigurationRequestMessage = {
  domain: 'sse_configuration_request';
};

export type SseConfigurationResponseMessage = {
  domain: 'sse_configuration_response';
  upstreamUrl: string;
  headers: Record<string, string>;
};

export type SseConnectionStatusMessage = {
  domain: 'sse_connection_status';
  status: 'connected' | 'disconnected' | 'error';
  detail?: unknown;
};

/**
 * worker requires the client to log messages to datadog
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type SseLogMessage = {
  domain: 'log';
  level: LogLevel;
  message: string;
  args: unknown[];
};

export type SsePingMessage = {
  domain: 'ping';
};

export type SsePongMessage = {
  domain: 'pong';
};

export type SseHeartbeatMessage = Extract<SseStreamResponseDto, { domain: 'heartbeat' }>;
export const HEARTBEAT_DEADLINE = 60_000;
export const HEARTBEAT_INTERVAL = HEARTBEAT_DEADLINE + 1_000;

/**
 * messages sent from upstream to worker
 */
export type SseUpstreamMessage = SseStreamResponseDto;

/**
 * messages sent from worker to clients
 */
export type SseWorkerMessage =
  | // clients do not need to know about heartbeats
  Exclude<SseStreamResponseDto, SseHeartbeatMessage>
  | SseConfigurationRequestMessage
  | SseConnectionStatusMessage
  | SsePingMessage
  | SseLogMessage;

export const sendWorkerMessage = (port: MessagePort, message: SseWorkerMessage) => {
  port.postMessage(message);
};

/**
 * messages sent from clients to worker
 */
export type SseClientMessage = SseConfigurationResponseMessage | SsePongMessage;

export const sendClientMessage = (port: MessagePort, message: SseClientMessage) => {
  port.postMessage(message);
};

/**
 * messages sent to consumers of the client
 */
export type SseConsumerMessage =
  | // not sure where this should be handled yet
  SseConnectionStatusMessage
  | Exclude<SseStreamResponseDto, SseHeartbeatMessage>;
